<template>
  <div class="home">
    <!-- 背景层 -->
    <div class="home-bg-fixed"></div>
    <div class="home-bg-mask"></div>

    <!-- 简单反馈提示 -->
    <div v-if="showFeedback" class="simple-feedback" :class="feedbackType">
      {{ feedbackMessage }}
    </div>

        <!-- 主要内容 -->
    <div class="home-content">
      <!-- 🌳 爱心树区域 -->
      <div class="tree-section">
        <TreeCanvas />
      </div>

      <!-- 💕 核心爱心区域 -->
      <div class="heart-main-area">
        <HeartCenter
          ref="heartCenter"
          :received-miss-intensity="localMissIntensity"
          @miss-expressed="onMissExpressed"
          @miss-received="onMissReceived"
        />

        <!-- 🎈 装饰性元素 -->
        <div class="decorative-elements">
          <!-- 浮动粒子 -->
          <div class="floating-particles">
            <div v-for="i in 8" :key="i" class="particle" :style="getParticleStyle(i)"></div>
          </div>

          <!-- 思念波纹 -->
          <div class="miss-waves" :class="{ 'waves-active': localMissIntensity > 0 }">
            <div class="wave wave-1"></div>
            <div class="wave wave-2"></div>
            <div class="wave wave-3"></div>
          </div>
        </div>
      </div>

      <!-- 💝 诗意文字区域 -->
      <div class="poetry-section">
        <!-- 主标题 - 左侧 -->
        <div class="main-poetry left-align">
          <h2 class="poetry-title">愿时光温柔以待</h2>
          <p class="poetry-subtitle">即使分别，思念如初</p>
        </div>

        <!-- 统计信息 - 右侧错位 -->
        <div class="stats-poetry right-align">
          <div class="stat-line">
            <span class="stat-label">认识了</span>
            <span class="stat-number">{{ daysTogether }}</span>
            <span class="stat-label">天</span>
            <div class="stat-dot"></div>
          </div>
          <div class="stat-line">
            <span class="stat-number">{{ messageCount }}</span>
            <span class="stat-label">条留言</span>
            <div class="stat-dot"></div>
          </div>
          <div class="stat-line">
            <span class="stat-number">{{ orchardCount }}</span>
            <span class="stat-label">个瞬间</span>
            <div class="stat-dot"></div>
          </div>
          <div class="stat-line">
            <span class="stat-label">你们想念了</span>
            <span class="stat-number">{{ missCount }}</span>
            <span class="stat-label">次</span>
            <div class="stat-dot"></div>
          </div>
        </div>

        <!-- 底部诗句 - 居中 -->
        <div class="bottom-poetry center-align">
          <p class="final-verse">有则锦上添花，无则依旧风华</p>
          <div class="verse-decoration">✨</div>
        </div>
      </div>

      <!-- 💝 思念状态提示 -->
      <div v-if="localMissIntensity > 0" class="miss-notification">
        <div class="notification-content">
          <span class="notification-icon">💕</span>
          <span class="notification-text">{{ localMissIntensity }} 份思念</span>
        </div>
      </div>

      <!-- 给低部留出空间 -->
      <div style="height: 126px;"></div>
    </div>
  </div>
</template>

<script>
import TreeCanvas from '@/components/TreeCanvas.vue'
import HeartCenter from '@/components/home/HeartCenter.vue'
import { dataRecord } from '@/api/login.js'
import {
  sendMiss,
  confirmReceived
} from '@/api/miss.js'

export default {
  name: 'HomeView',
  components: {
    TreeCanvas,
    HeartCenter
  },
  data () {
    return {
      // 本地思念强度（从全局同步）
      localMissIntensity: 0,

      // 简单反馈系统
      showFeedback: false,
      feedbackMessage: '',
      feedbackType: 'success',

      // 📊 页面数据
      daysTogether: 0, // 在一起的天数
      messageCount: 0, // 消息数量
      orchardCount: 0, // 果园瞬间数量
      missCount: 0 // 思念次数
    }
  },

  mounted () {
    // 监听全局事件
    this.$root.$on('global-new-miss-received', this.handleGlobalNewMiss)
    this.$root.$on('global-miss-confirmed', this.handleGlobalMissConfirmed)
    this.$root.$on('global-send-miss-request', this.handleGlobalSendMissRequest)
    this.$root.$on('global-confirm-miss-request', this.handleGlobalConfirmMissRequest)
    this.$root.$on('global-miss-intensity-response', this.handleGlobalMissIntensityResponse)

    // 获取首页 统计数据
    this.dataRecord()

    // 初始化本地状态（延迟确保App.vue完全加载）
    this.$nextTick(() => {
      this.syncFromGlobalState()

      // 额外的延迟同步，确保万无一失
      setTimeout(() => {
        this.syncFromGlobalState()
      }, 200)
    })
  },

  beforeDestroy () {
    // 清理事件监听
    this.$root.$off('global-new-miss-received', this.handleGlobalNewMiss)
    this.$root.$off('global-miss-confirmed', this.handleGlobalMissConfirmed)
    this.$root.$off('global-send-miss-request', this.handleGlobalSendMissRequest)
    this.$root.$off('global-confirm-miss-request', this.handleGlobalConfirmMissRequest)
    this.$root.$off('global-miss-intensity-response', this.handleGlobalMissIntensityResponse)
  },

  methods: {
    // 获取首页 统计数据
    async dataRecord () {
      try {
        const res = await dataRecord()
        if (res.code === 200) {
          this.daysTogether = res.data.daysTogether
          this.messageCount = res.data.messageCount
          this.orchardCount = res.data.postCount
          this.missCount = res.data.missCount
        }
      } catch (error) {
        console.error('获取首页统计数据失败:', error)
      }
    },

    // 处理爱心表达思念
    onMissExpressed () {
      this.sendMissMessage()
    },

    // 处理爱心确认收到
    onMissReceived (data) {
      // 更新本地状态
      this.localMissIntensity = data.newIntensity

      // 通知全局更新
      this.$root.$emit('update-miss-intensity', data.newIntensity)

      // 发送确认API
      this.sendReceivedMessage()
    },

    // 发送思念消息
    async sendMissMessage () {
      try {
        await sendMiss({
          intensity: 1,
          message: '想你了'
        })
        console.log('思念发送成功')
        this.missCount++
      } catch (error) {
        console.error('发送思念失败:', error)
        this.showSimpleFeedback('发送失败', 'error')
      }
    },

    // 发送确认收到消息
    async sendReceivedMessage () {
      try {
        await confirmReceived({
          clearAmount: 1,
          isComplete: this.localMissIntensity === 0
        })
        console.log('确认收到成功')
      } catch (error) {
        console.error('确认收到失败:', error)
        this.showSimpleFeedback('确认失败', 'error')
      }
    },

    // 处理全局新思念事件
    handleGlobalNewMiss (data) {
      this.localMissIntensity = data.totalIntensity
      console.log('HomeView: 收到全局新思念，本地强度已更新为:', this.localMissIntensity)
    },

    // 处理全局思念确认事件
    handleGlobalMissConfirmed (data) {
      console.log('HomeView: 收到全局思念确认反馈')

      // 🎯 通知爱心中心减少发送强度
      if (this.$refs && this.$refs.heartCenter) {
        this.$refs.heartCenter.handleMissConfirmed()
      }
    },

    // 处理全局发送思念请求
    handleGlobalSendMissRequest () {
      // 如果当前在HomeView，执行发送逻辑
      if (this.$route.name === 'home' || this.$route.path === '/') {
        this.sendMissMessage()
      }
    },

    // 处理全局确认思念请求
    handleGlobalConfirmMissRequest (data) {
      // 如果当前在HomeView，更新本地状态
      if (this.$route.name === 'home' || this.$route.path === '/') {
        this.localMissIntensity = data.newIntensity
      }
    },

    // 从全局状态同步本地状态
    syncFromGlobalState () {
      // 直接从App.vue获取状态（更可靠）
      const appComponent = this.$root.$children[0] // App.vue是根组件的第一个子组件
      if (appComponent && appComponent.globalMissIntensity !== undefined) {
        this.localMissIntensity = appComponent.globalMissIntensity
        console.log('HomeView: 直接同步全局状态为:', this.localMissIntensity)
      } else {
        // 如果直接访问失败，使用事件方式 + 延迟
        this.$nextTick(() => {
          setTimeout(() => {
            this.$root.$emit('request-global-miss-intensity')
          }, 100)
        })
      }
    },

    // 处理全局状态响应
    handleGlobalMissIntensityResponse (intensity) {
      this.localMissIntensity = intensity
      console.log('HomeView: 已同步全局思念强度为:', intensity)
    },

    // 显示简单反馈
    showSimpleFeedback (message, type = 'success') {
      this.feedbackMessage = message
      this.feedbackType = type
      this.showFeedback = true

      setTimeout(() => {
        this.showFeedback = false
      }, 2000)
    },

    // 🎈 获取粒子样式
    getParticleStyle (index) {
      const angles = [0, 45, 90, 135, 180, 225, 270, 315]
      const angle = angles[index - 1] || 0
      const delay = index * 0.6
      const size = 3 + Math.random() * 4

      // 根据思念状态调整粒子颜色
      let colorStart, colorMid, colorEnd
      if (this.localMissIntensity > 0) {
        // 有思念时 - 粉红色系
        colorStart = 'rgba(236, 72, 153, 0.5)'
        colorMid = 'rgba(255, 105, 180, 0.4)'
        colorEnd = 'rgba(255, 182, 193, 0.3)'
      } else {
        // 正常状态 - 紫色系
        colorStart = 'rgba(139, 92, 246, 0.4)'
        colorMid = 'rgba(168, 85, 247, 0.3)'
        colorEnd = 'rgba(196, 181, 253, 0.2)'
      }

      return {
        '--angle': `${angle}deg`,
        '--delay': `${delay}s`,
        '--size': `${size}px`,
        '--color-start': colorStart,
        '--color-mid': colorMid,
        '--color-end': colorEnd
      }
    }
  },

  // 监听路由变化，确保每次进入都同步状态
  watch: {
    '$route' (to, from) {
      if (to.path === '/' || to.name === 'home') {
        // 进入home页面时重新同步状态
        this.$nextTick(() => {
          this.syncFromGlobalState()
        })
      }
    }
  }
}
</script>

<style scoped>
/* 主容器 */
.home {
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  min-height: 100vh;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.home::-webkit-scrollbar {
  display: none;
}

/* 背景层 */
.home-bg-fixed {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  pointer-events: none;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.home-bg-mask {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(
    to bottom,
    rgba(255,255,255,0.15) 0%,
    rgba(255,255,255,0.25) 50%,
    rgba(255,255,255,0.20) 100%
  );
  z-index: 1;
  pointer-events: none;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* 主要内容 */
.home-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 40px 20px;
  box-sizing: border-box;
  transform: translateZ(0);
  will-change: transform;
}

/* 🌳 爱心树区域 */
.tree-section {
  position: relative;
  z-index: 1;
  margin-bottom: 60px;
  width: 100%;
  display: flex;
  justify-content: center;
}

/* 💕 核心爱心区域 */
.heart-main-area {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px; /* 减少下边距 */
  margin-top: 10px; /* 减少上边距 */
  transform: scale(1.4); /* 稍微减小放大倍数 */
  transform-origin: center; /* 以中心为缩放原点 */
}

/* 简单反馈提示 */
.simple-feedback {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  padding: 12px 24px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  backdrop-filter: blur(20px);
  animation: fadeInOut 2s ease-in-out;
}

.simple-feedback.success {
  background: rgba(16, 185, 129, 0.9);
  color: white;
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.3);
}

.simple-feedback.error {
  background: rgba(239, 68, 68, 0.9);
  color: white;
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.3);
}

/* 💝 诗意文字区域 */
.poetry-section {
  position: relative;
  z-index: 3;
  width: 100%;
  max-width: 500px;
  margin: 10px 0 20px 0; /* 文字往上移动 */
  padding: 0 20px;
}

/* 主标题区域 - 左侧对齐 */
.main-poetry.left-align {
  text-align: left;
  margin-bottom: 40px;
  padding-left: 30px; /* 更向左 */
  animation: fadeInLeft 1.2s ease-out; /* 动画变慢 */
}

.poetry-title {
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', sans-serif;
  font-size: 1.8rem;
  font-weight: 300;
  color: #4A5568;
  margin: 0 0 8px 0;
  letter-spacing: 1.5px;
  line-height: 1.4;
  text-shadow: 0 1px 3px rgba(255,255,255,0.8);
}

.poetry-subtitle {
  font-size: 1rem;
  color: #718096;
  margin: 0;
  letter-spacing: 0.8px;
  opacity: 0.85;
  font-weight: 400;
  line-height: 1.6;
}

/* 统计信息区域 - 右侧错位 */
.stats-poetry.right-align {
  text-align: right;
  margin-bottom: 50px;
  padding-right: 40px; /* 更向右 */
  animation: fadeInRight 1.4s ease-out 0.3s both; /* 动画变慢，延迟增加 */
}

.stat-line {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 12px;
  position: relative;
}

.stat-number {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  color: #6366F1;
  margin-right: 6px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
}

.stat-label {
  font-size: 0.9rem;
  color: #6B7280;
  font-weight: 400;
  letter-spacing: 0.3px;
  margin-right: 12px;
}

.stat-dot {
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #6366F1, #EC4899);
  border-radius: 50%;
  animation: gentlePulse 2s ease-in-out infinite;
}

.stat-dot:nth-child(3n) {
  animation-delay: 0.5s;
}

.stat-dot:nth-child(3n+1) {
  animation-delay: 1s;
}

/* 底部诗句 - 居中 */
.bottom-poetry.center-align {
  text-align: center;
  animation: fadeInUp 1.0s ease-out 0.6s both; /* 动画变慢，延迟增加 */
}

.final-verse {
  font-family: '华文楷体', 'KaiTi', 'STKaiti', serif;
  font-size: 1rem;
  color: #718096;
  margin: 0 0 8px 0;
  letter-spacing: 2px;
  line-height: 1.6;
  opacity: 0.9;
}

.verse-decoration {
  font-size: 1.2rem;
  opacity: 0.6;
  animation: gentleTwinkle 3s ease-in-out infinite;
}

/* 💝 思念状态提示 */
.miss-notification {
  position: relative;
  z-index: 4;
  display: flex;
  justify-content: center;
  animation: slideInUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  margin-top: 20px;
}

.notification-content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg,
    rgba(236, 72, 153, 0.1) 0%,
    rgba(255, 182, 193, 0.08) 100%
  );
  border: 1px solid rgba(236, 72, 153, 0.15);
  border-radius: 25px;
  backdrop-filter: blur(15px);
  box-shadow:
    0 8px 32px rgba(236, 72, 153, 0.1),
    0 4px 16px rgba(0, 0, 0, 0.02),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  animation: gentlePulse 2s ease-in-out infinite;
}

.notification-icon {
  font-size: 1.1rem;
  animation: heartBeat 1.5s ease-in-out infinite;
}

.notification-text {
  font-size: 0.9rem;
  color: rgba(236, 72, 153, 0.9);
  font-weight: 500;
  letter-spacing: 0.3px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif;
}

/* 🎈 装饰性元素 */
.decorative-elements {
  position: absolute;
  top: 50%; /* 精确定位到爱心中心 */
  left: 50%;
  width: 0; /* 设为0，使用transform定位 */
  height: 0;
  transform: translate(-50%, calc(-50% - 24px)); /* 中心对齐并往上移动12px */
  pointer-events: none;
  overflow: visible;
}

.floating-particles {
  position: absolute;
  top: 0; /* 相对于装饰容器中心 */
  left: 0;
  transform: scale(1.1); /* 减小粒子范围 */
  width: 160px; /* 缩小粒子容器 */
  height: 160px;
  margin-left: -80px; /* 左偏移宽度的一半 */
  margin-top: -80px; /* 上偏移高度的一半 */
  z-index: 1; /* 确保在爱心下方 */
}

.particle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--size);
  height: var(--size);
  background: linear-gradient(45deg,
    var(--color-start) 0%,
    var(--color-mid) 50%,
    var(--color-end) 100%
  );
  border-radius: 50%;
  transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-65px); /* 粒子更靠近爱心 */
  animation: particleFloat 8s ease-in-out infinite;
  animation-delay: var(--delay);
  box-shadow: 0 0 8px var(--color-start);
  transition: all 0.8s ease-in-out;
}

.miss-waves {
  position: absolute;
  top: 0; /* 相对于装饰容器中心 */
  left: 0;
  transform: scale(1.1); /* 减小波纹范围 */
  width: 240px; /* 缩小波纹容器 */
  height: 240px;
  margin-left: -120px; /* 左偏移宽度的一半 */
  margin-top: -120px; /* 上偏移高度的一半 */
  z-index: 1; /* 确保在爱心下方 */
}

.wave {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(139, 92, 246, 0.15);
  border-radius: 50%;
  animation: waveExpand 4s ease-out infinite;
  box-shadow: 0 0 20px rgba(139, 92, 246, 0.1);
  transition: all 0.8s ease-in-out;
}

/* 有思念时的波纹效果 */
.waves-active .wave {
  border-color: rgba(236, 72, 153, 0.2);
  box-shadow: 0 0 25px rgba(236, 72, 153, 0.15);
  animation-duration: 3s; /* 加快频率 */
}

.wave-1 {
  width: 120px;
  height: 120px;
  animation-delay: 0s;
}

.wave-2 {
  width: 160px;
  height: 160px;
  animation-delay: 1.3s;
}

.wave-3 {
  width: 200px;
  height: 200px;
  animation-delay: 2.6s;
}

/* 🎭 动画定义 */
@keyframes gentlePulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.02);
    opacity: 1;
  }
}

@keyframes heartBeat {
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.1); }
  50% { transform: scale(1); }
  75% { transform: scale(1.05); }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

@keyframes particleFloat {
  0%, 100% {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-80px) scale(0.8);
    opacity: 0.3;
  }
  25% {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-90px) scale(0.9);
    opacity: 0.5;
  }
  50% {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-100px) scale(1);
    opacity: 0.7;
  }
  75% {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-95px) scale(0.95);
    opacity: 0.5;
  }
}

@keyframes waveExpand {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.4;
  }
  50% {
    opacity: 0.2;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.8);
    opacity: 0;
  }
}

@keyframes gentleTwinkle {
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1) rotate(5deg);
    opacity: 1;
  }
}

@keyframes slideInUp {
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeInLeft {
  0% {
    transform: translateX(-100px); /* 从屏幕左侧更远开始 */
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeInRight {
  0% {
    transform: translateX(100px); /* 从屏幕右侧更远开始 */
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeInUp {
  0% {
    transform: translateY(40px); /* 从下方更远开始 */
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* 📱 移动端优化 */
@media (max-width: 600px) {
  .home-content {
    padding: 30px 16px;
  }

  .tree-section {
    margin-bottom: 40px;
  }

  .heart-main-area {
    margin-bottom: 15px; /* 减少移动端下边距 */
    margin-top: 20px; /* 合理的移动端上边距 */
    transform: scale(1.2); /* 移动端适度放大 */
  }

  /* 诗意文字区域移动端优化 */
  .poetry-section {
    margin: 5px 0 15px 0; /* 移动端文字也往上移动 */
    padding: 0 16px;
    max-width: 100%;
  }

  .main-poetry.left-align {
    margin-bottom: 30px;
    text-align: center; /* 移动端改为居中 */
    padding-left: 0; /* 移动端取消左边距 */
  }

  .poetry-title {
    font-size: 1.5rem;
    letter-spacing: 1px;
    line-height: 1.3;
  }

  .poetry-subtitle {
    font-size: 0.9rem;
    letter-spacing: 0.5px;
  }

  .stats-poetry.right-align {
    text-align: center; /* 移动端改为居中 */
    margin-bottom: 30px;
    padding-right: 0; /* 移动端取消右边距 */
  }

  .stat-line {
    justify-content: center; /* 移动端居中对齐 */
    margin-bottom: 10px;
  }

  .stat-number {
    font-size: 1.2rem;
  }

  .stat-label {
    font-size: 0.8rem;
  }

  .final-verse {
    font-size: 0.9rem;
    letter-spacing: 1.5px;
  }

  .verse-decoration {
    font-size: 1rem;
  }

  .notification-content {
    padding: 10px 16px;
    border-radius: 20px;
  }

  .notification-text {
    font-size: 0.85rem;
  }

  /* 移动端粒子优化 */
  .floating-particles {
    width: 120px; /* 移动端进一步缩小 */
    height: 120px;
    transform: scale(1.0); /* 移动端减小缩放 */
    margin-left: -60px; /* 移动端左偏移宽度的一半 */
    margin-top: -60px; /* 移动端上偏移高度的一半 */
  }

  .particle {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-50px); /* 移动端粒子更靠近爱心 */
  }

  .miss-waves {
    width: 180px; /* 移动端进一步缩小 */
    height: 180px;
    transform: scale(1.0); /* 移动端减小缩放 */
    margin-left: -90px; /* 移动端左偏移宽度的一半 */
    margin-top: -90px; /* 移动端上偏移高度的一半 */
  }

  /* 移动端减少装饰元素 */
  .decorative-elements {
    opacity: 0.7;
  }

  .particle:nth-child(n+5) {
    display: none; /* 移动端只显示前4个粒子 */
  }

  /* 移动端动画幅度调整 */
  @keyframes fadeInLeft {
    0% {
      transform: translateX(-60px); /* 移动端减小幅度 */
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes fadeInRight {
    0% {
      transform: translateX(60px); /* 移动端减小幅度 */
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes fadeInUp {
    0% {
      transform: translateY(30px); /* 移动端减小幅度 */
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }
}

/* 反馈动画 */
@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
  20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
}

/* 全局优化 */
* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
